// backup/frontend/src/section/become_member/user/view/user-view.jsx
import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import TableNoData from '../table-no-data';
import UserTableRow from '../user-table-row';
import UserTableHead from '../user-table-head';
import TableEmptyRows from '../table-empty-rows';
import UserTableToolbar from '../user-table-toolbar';
import { applyFilter, getComparator } from '../utils';
import { getErrorMessage } from '../../../../utils';
import { toast } from 'react-toastify';
import becomeMemberServices from '../../../../services/becomeMemberServices';
import { TableHeaderStyle } from '../../../../theme/palette';
import MembershipApplicationModal from '../../../../components/MembershipApplicationModal';
import AdminRegisterMember from '../../AdminRegisterMember';

// ----------------------------------------------------------------------

const ROWS_PER_PAGE = 20;

export default function BecomeMember() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [filterName, setFilterName] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // View modal state
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editApplication, setEditApplication] = useState(null);

  // Register modal state
  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  const handleSort = (event, id) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') { setOrder(isAsc ? 'desc' : 'asc'); setOrderBy(id); }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = dataPaginated?.map((n) => n._id) || [];
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0) newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    setSelected(newSelected);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const getData = () => {
    setLoading(true);
    becomeMemberServices
      .getApplications()
      .then((res) => { setData(res?.data?.data || []); })
      .catch((err) => { toast.error(getErrorMessage(err)); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { getData(); }, []);

  const dataFiltered = applyFilter({
    inputData: data,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const totalFiltered = dataFiltered.length;
  const dataPaginated = dataFiltered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const notFound = !loading && totalFiltered === 0;

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleDeleteRow = (row) => {
    becomeMemberServices.deleteApplication(row._id).then(() => {
      toast.success('Application deleted');
      getData();
    }).catch((err) => { toast.error(getErrorMessage(err)); });
  };

  const handleViewRow = async (row) => {
    try {
      const response = await becomeMemberServices.getApplicationByID(row._id);
      setSelectedApplication(response.data.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Failed to load application details');
      console.error('Error loading application:', error);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedApplication(null);
  };

  // Open edit modal — reuse same data already loaded from view, or fetch fresh
  const handleEditRow = async (row) => {
    try {
      const response = await becomeMemberServices.getApplicationByID(row._id);
      setEditApplication(response.data.data);
      setEditModalOpen(true);
    } catch (error) {
      toast.error('Failed to load application for editing');
      console.error('Error loading application for edit:', error);
    }
  };

  // Called when edit form succeeds — close modal, refresh list
  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setEditApplication(null);
    setTimeout(() => getData(), 300);
  };

  // Called when register form succeeds — close modal, refresh list
  const handleRegisterSuccess = () => {
    setRegisterModalOpen(false);
    setTimeout(() => getData(), 300);
  };

  const handleDownloadPDF = async (row) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${baseURL}/admin/membership-applications/${row._id}/pdf`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', errorText);
        throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `membership_application_${row.applicant?.first_name}_${row.applicant?.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
    }
  };

  const startRow = totalFiltered === 0 ? 0 : page * ROWS_PER_PAGE + 1;
  const endRow = Math.min((page + 1) * ROWS_PER_PAGE, totalFiltered);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Header row with Register Member button ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Stack>
          <Typography variant="h4">Membership Applications</Typography>
          <div style={{ backgroundColor: TableHeaderStyle.border, height: '2px' }} />
        </Stack>

        <Button
          variant="contained"
          onClick={() => setRegisterModalOpen(true)}
          sx={{
            backgroundColor: '#28a745',
            '&:hover': { backgroundColor: '#218838' },
            fontWeight: 'bold',
            fontSize: '14px',
            padding: '10px 20px',
            borderRadius: '8px',
          }}
        >
          + Register Member
        </Button>
      </Stack>

      {/* ── Applications table ── */}
      <Card>
        <UserTableToolbar
          numSelected={selected.length}
          filterName={filterName}
          onFilterName={handleFilterByName}
        />

        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
          {loading ? 'Loading...' : `Showing ${startRow}–${endRow} of ${totalFiltered} records`}
        </Typography>

        <TableContainer sx={{ overflow: { xs: 'auto', md: 'visible' } }}>
          <Table sx={{ minWidth: { xs: 300, sm: 600, md: 800 }, width: '100%', tableLayout: { md: 'fixed' } }}>
            <UserTableHead
              order={order}
              orderBy={orderBy}
              rowCount={dataPaginated?.length}
              numSelected={selected.length}
              onRequestSort={handleSort}
              onSelectAllClick={handleSelectAllClick}
              headLabel={[
                { id: '#', label: '#', width: '5%' },
                { id: 'applicant.first_name', label: 'Applicant Name', width: '22%' },
                { id: 'applicant.email', label: 'Email', width: '28%' },
                { id: 'applicant.phone', label: 'Phone', width: '18%' },
                { id: 'createdAt', label: 'Submitted Date', width: '17%' },
                { id: '', width: '5%' },
              ]}
            />
            <TableBody>
              {dataPaginated.map((row, index) => (
                <UserTableRow
                  key={row._id}
                  rowNumber={page * ROWS_PER_PAGE + index + 1}
                  applicantName={`${row.applicant?.first_name || ''} ${row.applicant?.last_name || ''}`}
                  email={row.applicant?.email}
                  phone={row.applicant?.phone}
                  submittedDate={new Date(row.createdAt).toLocaleDateString()}
                  onDelete={() => handleDeleteRow(row)}
                  onView={() => handleViewRow(row)}
                  onEdit={() => handleEditRow(row)}
                  onDownloadPDF={() => handleDownloadPDF(row)}
                />
              ))}
              <TableEmptyRows height={77} emptyRows={0} />
              {!loading && notFound && <TableNoData query={filterName} />}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalFiltered}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
        />
      </Card>

      {/* ── View Details Modal (existing) ── */}
      <MembershipApplicationModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        application={selectedApplication}
      />

      {/* ── Register Member Modal ── */}
      <Dialog
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Register New Member
          <IconButton onClick={() => setRegisterModalOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <AdminRegisterMember
            editMode={false}
            onSuccess={handleRegisterSuccess}
          />
        </DialogContent>
      </Dialog>

      {/* ── Edit Member Modal ── */}
      <Dialog
        open={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditApplication(null); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Member Application
          <IconButton onClick={() => { setEditModalOpen(false); setEditApplication(null); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {editApplication && (
            <AdminRegisterMember
              prefillData={editApplication}
              editMode={true}
              editId={editApplication._id}
              onSuccess={handleEditSuccess}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}